import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './ImageLightbox.css';

export type ImageLightboxProps = {
  open: boolean;
  urls: string[];
  index: number;
  getAlt: (i: number) => string;
  onClose: () => void;
  onIndexChange: (i: number) => void;
};

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  open,
  urls,
  index,
  getAlt,
  onClose,
  onIndexChange,
}) => {
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowLeft' && urls.length > 1) {
        e.preventDefault();
        onIndexChange((index - 1 + urls.length) % urls.length);
      } else if (e.key === 'ArrowRight' && urls.length > 1) {
        e.preventDefault();
        onIndexChange((index + 1) % urls.length);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, index, urls.length, onClose, onIndexChange]);

  if (!open || urls.length === 0) return null;

  const url = urls[index];
  if (!url) return null;

  return createPortal(
    <div
      className="image-lightbox-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Image gallery"
      onClick={onClose}
    >
      <div className="image-lightbox-inner" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="image-lightbox-close" aria-label="Close" onClick={onClose}>
          ×
        </button>
        {urls.length > 1 && (
          <>
            <button
              type="button"
              className="image-lightbox-nav image-lightbox-prev"
              aria-label="Previous image"
              onClick={() => onIndexChange((index - 1 + urls.length) % urls.length)}
            >
              ‹
            </button>
            <button
              type="button"
              className="image-lightbox-nav image-lightbox-next"
              aria-label="Next image"
              onClick={() => onIndexChange((index + 1) % urls.length)}
            >
              ›
            </button>
          </>
        )}
        <img src={url} alt={getAlt(index)} className="image-lightbox-img" />
        {urls.length > 1 && (
          <div className="image-lightbox-counter" aria-hidden>
            {index + 1} / {urls.length}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ImageLightbox;
